import React, { useState, useEffect } from 'react';
import { Table, Header, Segment, Message, Label, Tab, Modal, Button, List, Dropdown, Icon, Confirm } from 'semantic-ui-react';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { formatDate } from '../../utils/dateUtils';
import { Timestamp } from 'firebase/firestore';
import FinalResultModal from './FinalResultModal';
import ReassignModal from './ReassignModal';

interface User {
    id: string;
    firstName: string;
    lastName: string;
    initiatedName?: string;
    role: 'student' | 'grader';
}

interface Test {
    id: string;
    testName: string;
    type: 'openBook' | 'closeBook' | 'verseMemorization';
    lastSubmissionDate?: Timestamp | Date;
    examStatus?: 'notStarted' | 'inProgress' | 'ended';
}

interface Criteria {
    description: string;
    grade: number;
    actualGrade?: number;
}

interface Submission {
    studentId: string;
    submittedAt?: Date;
    assignedGraderId?: string;
    assignedGraderName?: string;
    assignedAt?: Date;
    gradingStatus?: 'pending' | 'graded';
    grade?: number;
    requiresRewrite?: boolean;
    essay?: string;
    criteriaGrades?: Criteria[];
    graderComments?: string;
    submitted?: boolean;
    pdfUrl?: string;
    answers?: { shortAnswer?: unknown[]; longAnswer?: unknown[] };
    answer?: string;
    grades?: {
        shortAnswer: number | Array<{ questionId: string; grade: number; maxGrade: number; comment?: string }>;
        longAnswer: number | Array<{ questionId: string; grade: number; maxGrade: number; comment?: string }>;
    };
    graderRemovedFromModule?: boolean;
    resumeAllowed?: boolean;
}

interface TestStatus {
    test: Test;
    submissions: Record<string, Submission>; // keyed by studentId
}

type FilterType = 'all' | 'notSubmitted' | 'notGraded';

const ViewStatus: React.FC<{ moduleId: string }> = ({ moduleId }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [students, setStudents] = useState<User[]>([]);
    const [graders, setGraders] = useState<Record<string, User>>({});
    const [allGraders, setAllGraders] = useState<Record<string, User>>({});
    const [testStatuses, setTestStatuses] = useState<TestStatus[]>([]);
    const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeFilter, setActiveFilter] = useState<FilterType>('all');
    const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
    const [isFinalResultModalOpen, setIsFinalResultModalOpen] = useState(false);
    const [isLoadingFinalResult, setIsLoadingFinalResult] = useState(false);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [moduleName, setModuleName] = useState<string>('');
    const [isExportingCSV, setIsExportingCSV] = useState(false);
    const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
    const [selectedReassignSubmission, setSelectedReassignSubmission] = useState<{
        submission: Submission;
        studentId: string;
        testId: string;
    } | null>(null);
    const [isExamLogsModalOpen, setIsExamLogsModalOpen] = useState(false);
    const [examLogs, setExamLogs] = useState<Array<{ id: string; type: string; detail: string; timestamp?: Date; imageUrl?: string }>>([]);
    const [examLogsLoading, setExamLogsLoading] = useState(false);
    const [examLogsStudentName, setExamLogsStudentName] = useState('');
    const [showReenableConfirm, setShowReenableConfirm] = useState(false);
    const [reenableTarget, setReenableTarget] = useState<{ studentId: string; studentName: string; testId: string } | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch module users (students)
                const moduleDoc = await getDoc(doc(db, 'modules', moduleId));
                const moduleData = moduleDoc.data();
                const moduleUsers = moduleData?.users || [];
                
                // Set module name for CSV export
                if (moduleData?.name) {
                    setModuleName(moduleData.name);
                }
                
                // Fetch all users' details
                const usersSnapshot = await getDocs(collection(db, 'users'));
                const usersMap = new Map<string, User>();
                usersSnapshot.docs.forEach(doc => {
                    const userData = doc.data() as User;
                    usersMap.set(doc.id, { ...userData, id: doc.id });
                });

                // Filter students and graders
                const studentsList: User[] = [];
                const gradersMap: Record<string, User> = {};
                
                moduleUsers.forEach((userId: string) => {
                    const user = usersMap.get(userId);
                    if (user) {
                        if (user.role === 'student') {
                            studentsList.push(user);
                        } else if (user.role === 'grader') {
                            gradersMap[userId] = user;
                        }
                    }
                });

                // Create a comprehensive grader map that includes all graders (not just module graders)
                const allGradersMap: Record<string, User> = {};
                usersSnapshot.docs.forEach(doc => {
                    const userData = doc.data() as User;
                    if (userData.role === 'grader') {
                        allGradersMap[doc.id] = { ...userData, id: doc.id };
                    }
                });

                setStudents(studentsList);
                setGraders(gradersMap);
                setAllGraders(allGradersMap);

                // Fetch tests
                const testsSnapshot = await getDocs(collection(db, 'modules', moduleId, 'tests'));
                const testStatusPromises = testsSnapshot.docs.map(async testDoc => {
                    const testData = testDoc.data();
                    const test = {
                        id: testDoc.id,
                        ...testData,
                        lastSubmissionDate: testData.lastSubmissionDate
                    } as Test;
                    
                    // Fetch submissions for this test
                    const submissionsSnapshot = await getDocs(
                        collection(db, 'modules', moduleId, 'tests', test.id, 'submissions')
                    );
                    
                    const submissions: Record<string, Submission> = {};
                    submissionsSnapshot.docs.forEach(subDoc => {
                        const subData = subDoc.data();
                        if (Array.isArray(subData.answers) && subData.answers[0]) {
                            // Open Book Test
                            const assignedGraderId = subData.answers[0].assignedGraderId;
                            submissions[subDoc.id] = {
                                studentId: subDoc.id,
                                submittedAt: subData.answers[0].submittedAt?.toDate(),
                                assignedGraderId: assignedGraderId,
                                assignedAt: subData.answers[0].assignedAt?.toDate(),
                                gradingStatus: subData.answers[0].gradingStatus,
                                grade: subData.answers[0].grade,
                                requiresRewrite: subData.answers[0].requiresRewrite,
                                essay: subData.answers[0].essay,
                                criteriaGrades: subData.answers[0].criteriaGrades,
                                graderComments: subData.answers[0].graderComments,
                                submitted: subData.answers[0].submitted,
                                graderRemovedFromModule: assignedGraderId ? !gradersMap[assignedGraderId] : false,
                            };
                        } else if (subData.answers?.shortAnswer || subData.answers?.longAnswer) {
                            // New Close Book Test (online answers)
                            const assignedGraderId = subData.assignedGraderId;
                            submissions[subDoc.id] = {
                                studentId: subDoc.id,
                                submittedAt: subData.submittedAt?.toDate(),
                                assignedGraderId: assignedGraderId,
                                assignedGraderName: subData.assignedGraderName,
                                assignedAt: subData.assignedAt?.toDate(),
                                gradingStatus: subData.gradingStatus,
                                grade: subData.grade,
                                graderComments: subData.graderComments,
                                submitted: true,
                                answers: subData.answers,
                                grades: subData.grades,
                                graderRemovedFromModule: assignedGraderId ? !gradersMap[assignedGraderId] : false,
                                resumeAllowed: subData.resumeAllowed || false,
                            };
                        } else if (subData.answer !== undefined) {
                            // New Verse Memorization (online answer)
                            const assignedGraderId = subData.assignedGraderId;
                            submissions[subDoc.id] = {
                                studentId: subDoc.id,
                                submittedAt: subData.submittedAt?.toDate(),
                                assignedGraderId: assignedGraderId,
                                assignedGraderName: subData.assignedGraderName,
                                assignedAt: subData.assignedAt?.toDate(),
                                gradingStatus: subData.gradingStatus,
                                grade: subData.grade,
                                graderComments: subData.graderComments,
                                submitted: true,
                                answer: subData.answer,
                                graderRemovedFromModule: assignedGraderId ? !gradersMap[assignedGraderId] : false,
                                resumeAllowed: subData.resumeAllowed || false,
                            };
                        } else if (subData.pdfUrl) {
                            // Legacy Close Book / Verse Memorization (PDF)
                            const assignedGraderId = subData.assignedGraderId;
                            submissions[subDoc.id] = {
                                studentId: subDoc.id,
                                submittedAt: subData.submittedAt?.toDate(),
                                assignedGraderId: assignedGraderId,
                                assignedGraderName: subData.assignedGraderName,
                                assignedAt: subData.assignedAt?.toDate(),
                                gradingStatus: subData.gradingStatus,
                                grade: subData.grade,
                                graderComments: subData.graderComments,
                                submitted: true,
                                pdfUrl: subData.pdfUrl,
                                grades: subData.grades,
                                graderRemovedFromModule: assignedGraderId ? !gradersMap[assignedGraderId] : false,
                            };
                        }
                    });

                    return { test, submissions };
                });

                const testStatuses = await Promise.all(testStatusPromises);
                setTestStatuses(testStatuses);
                
            } catch (err) {
                console.error('Error fetching status data:', err);
                setError('Failed to load status data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [moduleId]);

    // Add this function to sort and filter students
    const getFilteredStudents = (students: User[], submissions: Record<string, Submission>) => {
        // First, sort students by first name
        const sortedStudents = [...students].sort((a, b) => 
            a.firstName.localeCompare(b.firstName)
        );

        // Then apply filters
        return sortedStudents.filter(student => {
            const submission = submissions[student.id];
            switch (activeFilter) {
                case 'notSubmitted':
                    return !submission?.submitted;
                case 'notGraded':
                    return submission && submission.gradingStatus !== 'graded';
                default:
                    return true;
            }
        });
    };

    const filterOptions = [
        { key: 'all', text: 'All Students', value: 'all' },
        { key: 'notSubmitted', text: 'Not Submitted', value: 'notSubmitted' },
        { key: 'notGraded', text: 'Not Graded', value: 'notGraded' }
    ];

    const calculateGradingDeadline = (lastSubmissionDate: Timestamp | Date) => {
        // Convert Timestamp to Date if needed
        const submissionDate = lastSubmissionDate instanceof Timestamp 
            ? lastSubmissionDate.toDate() 
            : lastSubmissionDate;
            
        const deadline = new Date(submissionDate);
        deadline.setDate(deadline.getDate() + 14); // Add 14 days
        return deadline;
    };

    const handleReassignClick = (submission: Submission, studentId: string, testId: string) => {
        setSelectedReassignSubmission({ submission, studentId, testId });
        setIsReassignModalOpen(true);
    };

    const handleReassignSuccess = async (updatedSubmissionData: any, studentId: string, testId: string) => {
        console.log('handleReassignSuccess called with:', { updatedSubmissionData, studentId, testId });
        
        // Transform the updated submission data to match the expected structure
        let transformedSubmission: Submission;
        
        if (Array.isArray(updatedSubmissionData.answers) && updatedSubmissionData.answers[0]) {
            const assignedGraderId = updatedSubmissionData.answers[0].assignedGraderId;
            transformedSubmission = {
                studentId: studentId,
                submittedAt: updatedSubmissionData.answers[0].submittedAt?.toDate(),
                assignedGraderId: assignedGraderId,
                assignedAt: updatedSubmissionData.answers[0].assignedAt?.toDate(),
                gradingStatus: updatedSubmissionData.answers[0].gradingStatus,
                grade: updatedSubmissionData.answers[0].grade,
                requiresRewrite: updatedSubmissionData.answers[0].requiresRewrite,
                essay: updatedSubmissionData.answers[0].essay,
                criteriaGrades: updatedSubmissionData.answers[0].criteriaGrades,
                graderComments: updatedSubmissionData.answers[0].graderComments,
                submitted: updatedSubmissionData.answers[0].submitted,
                graderRemovedFromModule: assignedGraderId ? !graders[assignedGraderId] : false,
            };
        } else if (updatedSubmissionData.answers?.shortAnswer || updatedSubmissionData.answers?.longAnswer) {
            const assignedGraderId = updatedSubmissionData.assignedGraderId;
            transformedSubmission = {
                studentId: studentId,
                submittedAt: updatedSubmissionData.submittedAt?.toDate(),
                assignedGraderId: assignedGraderId,
                assignedGraderName: updatedSubmissionData.assignedGraderName,
                assignedAt: updatedSubmissionData.assignedAt?.toDate(),
                gradingStatus: updatedSubmissionData.gradingStatus,
                grade: updatedSubmissionData.grade,
                graderComments: updatedSubmissionData.graderComments,
                submitted: true,
                answers: updatedSubmissionData.answers,
                grades: updatedSubmissionData.grades,
                graderRemovedFromModule: assignedGraderId ? !graders[assignedGraderId] : false,
            };
        } else if (updatedSubmissionData.answer !== undefined) {
            const assignedGraderId = updatedSubmissionData.assignedGraderId;
            transformedSubmission = {
                studentId: studentId,
                submittedAt: updatedSubmissionData.submittedAt?.toDate(),
                assignedGraderId: assignedGraderId,
                assignedGraderName: updatedSubmissionData.assignedGraderName,
                assignedAt: updatedSubmissionData.assignedAt?.toDate(),
                gradingStatus: updatedSubmissionData.gradingStatus,
                grade: updatedSubmissionData.grade,
                graderComments: updatedSubmissionData.graderComments,
                submitted: true,
                answer: updatedSubmissionData.answer,
                graderRemovedFromModule: assignedGraderId ? !graders[assignedGraderId] : false,
            };
        } else if (updatedSubmissionData.pdfUrl) {
            const assignedGraderId = updatedSubmissionData.assignedGraderId;
            transformedSubmission = {
                studentId: studentId,
                submittedAt: updatedSubmissionData.submittedAt?.toDate(),
                assignedGraderId: assignedGraderId,
                assignedGraderName: updatedSubmissionData.assignedGraderName,
                assignedAt: updatedSubmissionData.assignedAt?.toDate(),
                gradingStatus: updatedSubmissionData.gradingStatus,
                grade: updatedSubmissionData.grade,
                requiresRewrite: updatedSubmissionData.requiresRewrite,
                pdfUrl: updatedSubmissionData.pdfUrl,
                grades: updatedSubmissionData.grades,
                graderRemovedFromModule: assignedGraderId ? !graders[assignedGraderId] : false,
            };
        } else {
            console.error('Unknown submission type:', updatedSubmissionData);
            return;
        }

        console.log('Transformed submission:', transformedSubmission);
        
        // Update the specific submission in the local state
        setTestStatuses(prevTestStatuses => 
            prevTestStatuses.map(testStatus => {
                if (testStatus.test.id === testId) {
                    console.log('Updating test status for testId:', testId, 'studentId:', studentId);
                    return {
                        ...testStatus,
                        submissions: {
                            ...testStatus.submissions,
                            [studentId]: transformedSubmission
                        }
                    };
                }
                return testStatus;
            })
        );
    };

    const renderSubmissionModal = () => (
        <Modal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            size="large"
        >
            <Modal.Header>
                Student Submission
                {selectedSubmission?.gradingStatus === 'graded' && (
                    <Label color="green" style={{ marginLeft: '1rem' }}>
                        Graded
                    </Label>
                )}
            </Modal.Header>
            <Modal.Content>
                {selectedSubmission?.essay ? (
                    <>
                        <Header as="h4">Submitted Essay</Header>
                        <Segment style={{ whiteSpace: 'pre-wrap' }}>
                            <div dangerouslySetInnerHTML={{ __html: selectedSubmission.essay }} />
                        </Segment>
                    </>
                ) : selectedSubmission?.answers ? (
                    <>
                        <Header as="h4">Submitted Answers</Header>
                        {(selectedSubmission.answers.shortAnswer as Array<{ questionId: string; answer: string }>)?.map((a, idx) => (
                            <Segment key={a.questionId}>
                                <p><strong>Short Answer {idx + 1}:</strong></p>
                                <p style={{ whiteSpace: 'pre-wrap' }}>{a.answer}</p>
                            </Segment>
                        ))}
                        {(selectedSubmission.answers.longAnswer as Array<{ questionId: string; answer: string }>)?.map((a, idx) => (
                            <Segment key={a.questionId}>
                                <p><strong>Long Answer {idx + 1}:</strong></p>
                                <p style={{ whiteSpace: 'pre-wrap' }}>{a.answer}</p>
                            </Segment>
                        ))}
                    </>
                ) : selectedSubmission?.answer !== undefined ? (
                    <>
                        <Header as="h4">Submitted Answer</Header>
                        <Segment style={{ whiteSpace: 'pre-wrap' }}>
                            {selectedSubmission.answer}
                        </Segment>
                    </>
                ) : selectedSubmission?.pdfUrl ? (
                    <>
                        <Header as="h4">Submitted PDF</Header>
                        <Button
                            as="a"
                            href={selectedSubmission.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            primary
                        >
                            View PDF
                        </Button>
                    </>
                ) : null}

                {selectedSubmission?.gradingStatus === 'graded' && (
                    <>
                        <Header as="h4">
                            Grade: {selectedSubmission.grade}
                            {selectedSubmission.requiresRewrite && (
                                <Label color="red" style={{ marginLeft: '1rem' }}>
                                    Requires Rewrite
                                </Label>
                            )}
                        </Header>

                        {selectedSubmission.criteriaGrades && (
                            <>
                                <Header as="h4">Criteria Grades</Header>
                                <List>
                                    {selectedSubmission.criteriaGrades.map((criterion, index) => (
                                        <List.Item key={index}>
                                            <List.Content>
                                                <List.Header>{criterion.description}</List.Header>
                                                <List.Description>
                                                    Grade: {criterion.actualGrade} / {criterion.grade}
                                                </List.Description>
                                            </List.Content>
                                        </List.Item>
                                    ))}
                                </List>
                            </>
                        )}

                        {selectedSubmission.grades && (
                            <>
                                <Header as="h4">Grade Breakdown</Header>
                                <List>
                                    {Array.isArray(selectedSubmission.grades.shortAnswer) ? (
                                        selectedSubmission.grades.shortAnswer.map((g: { questionId: string; grade: number; maxGrade: number; comment?: string }, idx: number) => (
                                            <List.Item key={g.questionId}>
                                                <List.Content>
                                                    <List.Header>Short Answer {idx + 1}</List.Header>
                                                    <List.Description>
                                                        Grade: {g.grade}/{g.maxGrade}
                                                        {g.comment && ` - ${g.comment}`}
                                                    </List.Description>
                                                </List.Content>
                                            </List.Item>
                                        ))
                                    ) : (
                                        <List.Item>
                                            <List.Content>
                                                <List.Header>Short Answer Questions</List.Header>
                                                <List.Description>
                                                    Grade: {selectedSubmission.grades.shortAnswer}
                                                </List.Description>
                                            </List.Content>
                                        </List.Item>
                                    )}
                                    {Array.isArray(selectedSubmission.grades.longAnswer) ? (
                                        selectedSubmission.grades.longAnswer.map((g: { questionId: string; grade: number; maxGrade: number; comment?: string }, idx: number) => (
                                            <List.Item key={g.questionId}>
                                                <List.Content>
                                                    <List.Header>Long Answer {idx + 1}</List.Header>
                                                    <List.Description>
                                                        Grade: {g.grade}/{g.maxGrade}
                                                        {g.comment && ` - ${g.comment}`}
                                                    </List.Description>
                                                </List.Content>
                                            </List.Item>
                                        ))
                                    ) : (
                                        <List.Item>
                                            <List.Content>
                                                <List.Header>Long Answer Questions</List.Header>
                                                <List.Description>
                                                    Grade: {selectedSubmission.grades.longAnswer}
                                                </List.Description>
                                            </List.Content>
                                        </List.Item>
                                    )}
                                </List>
                            </>
                        )}

                        <Header as="h4">Grader Comments</Header>
                        <Segment>
                            {selectedSubmission.graderComments || 'No comments provided'}
                        </Segment>
                    </>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={() => setIsModalOpen(false)}>Close</Button>
            </Modal.Actions>
        </Modal>
    );

    const handleExamLogsClick = async (studentId: string, studentName: string, testId: string) => {
        setExamLogsStudentName(studentName);
        setIsExamLogsModalOpen(true);
        setExamLogsLoading(true);
        setExamLogs([]);
        try {
            const examLogsRef = collection(
                db,
                'modules',
                moduleId,
                'tests',
                testId,
                'submissions',
                studentId,
                'exam-logs'
            );
            const snapshot = await getDocs(examLogsRef);
            const logs = snapshot.docs.map(docSnap => {
                const data = docSnap.data();
                return {
                    id: docSnap.id,
                    type: data.type || 'UNKNOWN',
                    detail: data.detail || '',
                    timestamp: data.timestamp?.toDate?.() ?? undefined,
                    imageUrl: data.imageUrl
                };
            });
            logs.sort((a, b) => {
                if (!a.timestamp) return 1;
                if (!b.timestamp) return -1;
                return a.timestamp.getTime() - b.timestamp.getTime();
            });
            setExamLogs(logs);
        } catch (err) {
            console.error('Failed to fetch exam logs:', err);
            setExamLogs([]);
        } finally {
            setExamLogsLoading(false);
        }
    };

    const handleReenableExam = async () => {
        if (!reenableTarget) return;
        try {
            const submissionRef = doc(
                db,
                'modules',
                moduleId,
                'tests',
                reenableTarget.testId,
                'submissions',
                reenableTarget.studentId
            );
            await updateDoc(submissionRef, { resumeAllowed: true });

            setTestStatuses(prev =>
                prev.map(ts => {
                    if (ts.test.id !== reenableTarget.testId) return ts;
                    const updatedSubmissions = { ...ts.submissions };
                    if (updatedSubmissions[reenableTarget.studentId]) {
                        updatedSubmissions[reenableTarget.studentId] = {
                            ...updatedSubmissions[reenableTarget.studentId],
                            resumeAllowed: true,
                        };
                    }
                    return { ...ts, submissions: updatedSubmissions };
                })
            );
        } catch (err) {
            console.error('Failed to re-enable exam:', err);
            setError('Failed to re-enable exam for student');
        } finally {
            setShowReenableConfirm(false);
            setReenableTarget(null);
        }
    };

    const renderExamLogsModal = () => (
        <Modal
            open={isExamLogsModalOpen}
            onClose={() => setIsExamLogsModalOpen(false)}
            size="large"
        >
            <Modal.Header>Exam Logs - {examLogsStudentName}</Modal.Header>
            <Modal.Content>
                {examLogsLoading ? (
                    <Message info>Loading exam logs...</Message>
                ) : examLogs.length === 0 ? (
                    <Message info>No exam logs found for this submission.</Message>
                ) : (
                    <>
                        <Header as="h4">Anti-Cheat Violations</Header>
                        <List divided>
                            {examLogs.map((log, idx) => (
                                <List.Item key={log.id}>
                                    <List.Content>
                                        <List.Header>
                                            {idx + 1}. {log.type}
                                            {log.timestamp && (
                                                <Label size="small" style={{ marginLeft: '0.5rem' }}>
                                                    {formatDate(log.timestamp)}
                                                </Label>
                                            )}
                                        </List.Header>
                                        <List.Description>{log.detail}</List.Description>
                                        {log.type === 'PROCTOR_SNAPSHOT' && log.imageUrl && (
                                            <div style={{ marginTop: '0.5rem' }}>
                                                <a href={log.imageUrl} target="_blank" rel="noopener noreferrer">
                                                    <img
                                                        src={log.imageUrl}
                                                        alt={`Proctor snapshot ${idx + 1}`}
                                                        style={{
                                                            maxWidth: '200px',
                                                            maxHeight: '150px',
                                                            borderRadius: '4px',
                                                            border: '1px solid #ddd',
                                                            cursor: 'pointer'
                                                        }}
                                                    />
                                                </a>
                                            </div>
                                        )}
                                    </List.Content>
                                </List.Item>
                            ))}
                        </List>
                    </>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={() => setIsExamLogsModalOpen(false)}>Close</Button>
            </Modal.Actions>
        </Modal>
    );

    const renderTestTable = (testStatus: TestStatus) => {
        const filteredStudents = getFilteredStudents(students, testStatus.submissions);
        
        return (
            <>
                <Segment>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <Dropdown
                                selection
                                options={filterOptions}
                                value={activeFilter}
                                onChange={(_, data) => setActiveFilter(data.value as FilterType)}
                                label="Filter"
                            />
                            <Label style={{ marginLeft: '1rem' }}>
                                Showing {filteredStudents.length} of {students.length} students
                            </Label>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            {testStatus.test.lastSubmissionDate && (
                                <>
                                    <div style={{ marginBottom: '0.5rem' }}>
                                        <Label size="large" color="blue">
                                            <Icon name="calendar" />
                                            Last Submission Date: {
                                                testStatus.test.lastSubmissionDate instanceof Timestamp
                                                    ? formatDate(testStatus.test.lastSubmissionDate.toDate())
                                                    : formatDate(testStatus.test.lastSubmissionDate)
                                            }
                                        </Label>
                                    </div>
                                    <div>
                                        <Label size="large" color="orange">
                                            <Icon name="clock" />
                                            Grading Deadline: {formatDate(calculateGradingDeadline(testStatus.test.lastSubmissionDate))}
                                        </Label>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </Segment>

                <Table celled>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Student</Table.HeaderCell>
                            <Table.HeaderCell>Submission Status</Table.HeaderCell>
                            <Table.HeaderCell>Submission Date</Table.HeaderCell>
                            <Table.HeaderCell>Assigned To</Table.HeaderCell>
                            <Table.HeaderCell>Assigned Date</Table.HeaderCell>
                            <Table.HeaderCell>Grading Status</Table.HeaderCell>
                            <Table.HeaderCell>Grade</Table.HeaderCell>
                            <Table.HeaderCell>Actions</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {filteredStudents.map(student => {
                            const submission = testStatus.submissions[student.id];
                            console.log("Submission", submission);
                            return (
                                <Table.Row key={student.id}>
                                    <Table.Cell>
                                        {student.initiatedName ? `${student.initiatedName}` : `${student.firstName} ${student.lastName}`}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {submission?.submitted ? (
                                            <Label color="green">Submitted</Label>
                                        ) : (
                                            <Label>Not Submitted</Label>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {submission?.submittedAt && formatDate(submission.submittedAt)}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {submission?.assignedGraderId ? (
                                            (() => {
                                                // First try to get name from submission (close book tests)
                                                if (submission.assignedGraderName) {
                                                    return (
                                                        <div>
                                                            <div>{submission.assignedGraderName}</div>
                                                            {submission.graderRemovedFromModule && (
                                                                <Label size="mini" color="red">Inactive</Label>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                
                                                // Fallback to grader lookup from all graders
                                                const grader = allGraders[submission.assignedGraderId];
                                                if (grader) {
                                                    const graderName = grader.initiatedName || `${grader.firstName} ${grader.lastName}`;
                                                    const isRemovedFromModule = !graders[submission.assignedGraderId];
                                                    return (
                                                        <div>
                                                            <div>{graderName}</div>
                                                            {isRemovedFromModule && (
                                                                <Label size="mini" color="red">Inactive</Label>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                
                                                return (
                                                    <div>
                                                        <div style={{ color: '#ff6b6b' }}>Grader not found</div>
                                                        <Label size="mini" color="red">Inactive</Label>
                                                    </div>
                                                );
                                            })()
                                        ) : '-'}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {submission?.assignedAt && formatDate(submission.assignedAt)}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {submission?.gradingStatus && (
                                            <Label color={submission.gradingStatus === 'graded' ? 'green' : 'yellow'}>
                                                {submission.gradingStatus}
                                            </Label>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {submission?.grade !== undefined && (
                                            <>
                                                {submission.grade}
                                                {submission.requiresRewrite && (
                                                    <Label color="red" size="tiny" style={{ marginLeft: '0.5em' }}>
                                                        Rewrite
                                                    </Label>
                                                )}
                                            </>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                            {submission?.submitted && (
                                                <Button
                                                    primary
                                                    size="small"
                                                    onClick={() => {
                                                        setSelectedSubmission(submission);
                                                        setIsModalOpen(true);
                                                    }}
                                                >
                                                    View Submission
                                                </Button>
                                            )}
                                            {(testStatus.test.type === 'closeBook' || testStatus.test.type === 'verseMemorization') &&
                                             submission?.submitted && (
                                                <Button
                                                    size="small"
                                                    onClick={() => handleExamLogsClick(
                                                        student.id,
                                                        student.initiatedName || `${student.firstName} ${student.lastName}`,
                                                        testStatus.test.id
                                                    )}
                                                >
                                                    Exam Logs
                                                </Button>
                                            )}
                                            {submission?.submitted && 
                                             submission?.gradingStatus === 'pending' && 
                                             submission?.assignedGraderId && (
                                                <Button
                                                    color="orange"
                                                    size="small"
                                                    onClick={() => handleReassignClick(submission, student.id, testStatus.test.id)}
                                                >
                                                    Reassign
                                                </Button>
                                            )}
                                            {(testStatus.test.type === 'closeBook' || testStatus.test.type === 'verseMemorization') &&
                                             submission?.submitted &&
                                             testStatus.test.examStatus === 'inProgress' &&
                                             !submission?.resumeAllowed && (
                                                <Button
                                                    color="teal"
                                                    size="small"
                                                    onClick={() => {
                                                        setReenableTarget({
                                                            studentId: student.id,
                                                            studentName: student.initiatedName || `${student.firstName} ${student.lastName}`,
                                                            testId: testStatus.test.id
                                                        });
                                                        setShowReenableConfirm(true);
                                                    }}
                                                >
                                                    Re-enable Exam
                                                </Button>
                                            )}
                                            {submission?.resumeAllowed && (
                                                <Label color="blue" size="small">
                                                    Resume Allowed
                                                </Label>
                                            )}
                                        </div>
                                    </Table.Cell>
                                </Table.Row>
                            );
                        })}
                    </Table.Body>
                </Table>
            </>
        );
    };

    const exportToCSV = async () => {
        try {
            setIsExportingCSV(true);
            
            // Fetch all students' final results
            const csvData = await fetchAllStudentsFinalResults();
            
            // Create CSV content
            const csvContent = generateCSVContent(csvData);
            
            // Download CSV file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `${moduleName || 'Module'} Final Results.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
        } catch (error) {
            console.error('Error exporting CSV:', error);
            setError('Failed to export CSV');
        } finally {
            setIsExportingCSV(false);
        }
    };

    const fetchAllStudentsFinalResults = async () => {
        const results = [];
        
        for (const student of students) {
            try {
                // Fetch all tests in the module
                const testsSnapshot = await getDocs(collection(db, 'modules', moduleId, 'tests'));
                const testsData = testsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as any));

                // Fetch all submissions for the student
                const submissionsData: Record<string, any> = {};
                
                for (const test of testsData) {
                    try {
                        const submissionDoc = await getDoc(
                            doc(db, 'modules', moduleId, 'tests', test.id, 'submissions', student.id)
                        );
                        
                        if (submissionDoc.exists()) {
                            const submissionData = submissionDoc.data();
                            
                            if (test.type === 'openBook' && submissionData.answers?.[0]) {
                                const assignedGraderId = submissionData.answers[0].assignedGraderId;
                                submissionsData[test.id] = {
                                    studentId: student.id,
                                    submittedAt: submissionData.answers[0].submittedAt?.toDate(),
                                    gradingStatus: submissionData.answers[0].gradingStatus,
                                    grade: submissionData.answers[0].grade,
                                    requiresRewrite: submissionData.answers[0].requiresRewrite,
                                    graderComments: submissionData.answers[0].graderComments,
                                    submitted: submissionData.answers[0].submitted,
                                    assignedGraderId: assignedGraderId,
                                    graderRemovedFromModule: assignedGraderId ? !graders[assignedGraderId] : false,
                                };
                            } else if (test.type === 'closeBook' && (submissionData.answers?.shortAnswer || submissionData.answers?.longAnswer)) {
                                const assignedGraderId = submissionData.assignedGraderId;
                                submissionsData[test.id] = {
                                    studentId: student.id,
                                    submittedAt: submissionData.submittedAt?.toDate(),
                                    gradingStatus: submissionData.gradingStatus,
                                    grade: submissionData.grade,
                                    graderComments: submissionData.graderComments,
                                    submitted: true,
                                    answers: submissionData.answers,
                                    grades: submissionData.grades,
                                    assignedGraderId: assignedGraderId,
                                    assignedGraderName: submissionData.assignedGraderName,
                                    graderRemovedFromModule: assignedGraderId ? !graders[assignedGraderId] : false,
                                };
                            } else if (test.type === 'verseMemorization' && submissionData.answer !== undefined) {
                                const assignedGraderId = submissionData.assignedGraderId;
                                submissionsData[test.id] = {
                                    studentId: student.id,
                                    submittedAt: submissionData.submittedAt?.toDate(),
                                    gradingStatus: submissionData.gradingStatus,
                                    grade: submissionData.grade,
                                    graderComments: submissionData.graderComments,
                                    submitted: true,
                                    answer: submissionData.answer,
                                    assignedGraderId: assignedGraderId,
                                    assignedGraderName: submissionData.assignedGraderName,
                                    graderRemovedFromModule: assignedGraderId ? !graders[assignedGraderId] : false,
                                };
                            } else if ((test.type === 'closeBook' || test.type === 'verseMemorization') && submissionData.pdfUrl) {
                                const assignedGraderId = submissionData.assignedGraderId;
                                submissionsData[test.id] = {
                                    studentId: student.id,
                                    submittedAt: submissionData.submittedAt?.toDate(),
                                    gradingStatus: submissionData.gradingStatus,
                                    grade: submissionData.grade,
                                    graderComments: submissionData.graderComments,
                                    submitted: true,
                                    pdfUrl: submissionData.pdfUrl,
                                    grades: submissionData.grades,
                                    assignedGraderId: assignedGraderId,
                                    assignedGraderName: submissionData.assignedGraderName,
                                    graderRemovedFromModule: assignedGraderId ? !graders[assignedGraderId] : false,
                                };
                            }
                        }
                    } catch (err) {
                        console.error(`Error fetching submission for test ${test.id}:`, err);
                    }
                }

                // Fetch service log entries
                let serviceEntriesData = [];
                try {
                    const serviceDoc = await getDoc(doc(db, 'modules', moduleId, 'serviceLogs', student.id));
                    if (serviceDoc.exists()) {
                        const serviceData = serviceDoc.data();
                        const entries = serviceData.submissions || [];
                        serviceEntriesData = entries.map((entry: any) => ({
                            weekStartDate: entry.weekStartDate.toDate(),
                            hours: entry.hours,
                            supervisor: entry.supervisor,
                            description: entry.description,
                            submittedBy: entry.submittedBy,
                            submittedAt: entry.submittedAt.toDate()
                        }));
                    }
                } catch (err) {
                    console.error('Error fetching service entries:', err);
                }

                // Calculate final grade and module result
                const { finalGrade, moduleResult, failureReasons } = calculateFinalGradeForCSV(testsData, submissionsData, serviceEntriesData);

                results.push({
                    student,
                    tests: testsData,
                    submissions: submissionsData,
                    serviceEntries: serviceEntriesData,
                    finalGrade,
                    moduleResult,
                    failureReasons
                });
            } catch (error) {
                console.error(`Error fetching data for student ${student.id}:`, error);
            }
        }
        
        return results;
    };

    const calculateFinalGradeForCSV = (tests: any[], submissions: Record<string, any>, serviceEntries: any[]) => {
        const openBookTests = tests.filter(test => test.type === 'openBook');
        const closeBookTest = tests.find(test => test.type === 'closeBook');
        const verseTest = tests.find(test => test.type === 'verseMemorization');
        
        const failureReasons: string[] = [];
        let finalGrade = 0;
        let moduleResult = 'PASS';

        // Calculate Open Book Tests (40% of total grade)
        let openBookGrade = 0;
        if (openBookTests.length > 0) {
            let totalOpenBookGrade = 0;
            let allOpenBookPassed = true;
            
            for (const test of openBookTests) {
                const submission = submissions[test.id];
                if (!submission || !submission.submitted) {
                    failureReasons.push(`Open Book Test "${test.testName}" not submitted`);
                    allOpenBookPassed = false;
                } else if (submission.requiresRewrite || (submission.grade && submission.grade < 13)) {
                    failureReasons.push(`Open Book Test "${test.testName}" requires rewrite (grade: ${submission.grade}/20)`);
                    allOpenBookPassed = false;
                }
                
                if (submission && submission.grade) {
                    totalOpenBookGrade += submission.grade;
                }
            }
            
            if (openBookTests.length > 0) {
                openBookGrade = (totalOpenBookGrade / openBookTests.length) * 2;
            }
        }

        // Calculate Close Book Test (30% of total grade)
        let closeBookGrade = 0;
        if (closeBookTest) {
            const submission = submissions[closeBookTest.id];
            if (!submission || !submission.submitted) {
                failureReasons.push('Close Book Test not submitted');
            } else if (submission.grade !== undefined) {
                const closeBookPercentage = (submission.grade / closeBookTest.totalGrade) * 100;
                if (closeBookPercentage < 65) {
                    failureReasons.push(`Close Book Test grade too low: ${closeBookPercentage.toFixed(1)}% (minimum 65% required)`);
                }
                closeBookGrade = (closeBookPercentage / 100) * 30;
            }
        }

        // Calculate Verse Memorization (20% of total grade)
        let verseGrade = 0;
        if (verseTest) {
            const submission = submissions[verseTest.id];
            if (!submission || !submission.submitted) {
                failureReasons.push('Verse Memorization not submitted');
            } else if (submission.grade !== undefined) {
                const versePercentage = (submission.grade / verseTest.totalGrade) * 100;
                if (versePercentage < 65) {
                    failureReasons.push(`Verse Memorization grade too low: ${versePercentage.toFixed(1)}% (minimum 65% required)`);
                }
                verseGrade = (versePercentage / 100) * 20;
            }
        }

        // Calculate Service Hours (10% of total grade)
        const totalServiceHours = serviceEntries.reduce((total, entry) => total + entry.hours, 0);
        const serviceGrade = totalServiceHours >= 6 ? 10 : 0;

        // Calculate final grade
        finalGrade = openBookGrade + closeBookGrade + verseGrade + serviceGrade;

        // Determine module result
        if (failureReasons.length > 0) {
            moduleResult = 'REWRITE MODULE';
        } else if (finalGrade < 70) {
            moduleResult = 'FAIL';
            failureReasons.push(`Final grade too low: ${finalGrade.toFixed(1)}% (minimum 70% required)`);
        }

        return { finalGrade, moduleResult, failureReasons };
    };

    const generateCSVContent = (csvData: any[]) => {
        const headers = [
            'Student Name',
            'Module Result',
            'Final Grade (%)',
            'Service Hours',
            'Open Book Tests Status',
            'Close Book Test Status',
            'Verse Memorization Status',
            'Assigned Graders',
            'Failure Reasons'
        ];

        const rows = csvData.map(data => {
            const { student, tests, submissions, serviceEntries, finalGrade, moduleResult, failureReasons } = data;
            
            const openBookTests = tests.filter((test: any) => test.type === 'openBook');
            const closeBookTest = tests.find((test: any) => test.type === 'closeBook');
            const verseTest = tests.find((test: any) => test.type === 'verseMemorization');
            
            const totalServiceHours = serviceEntries.reduce((total: number, entry: any) => total + entry.hours, 0);
            
            const openBookStatus = openBookTests.map((test: any) => {
                const submission = submissions[test.id];
                if (!submission || !submission.submitted) return `${test.testName}: Not Submitted`;
                if (submission.requiresRewrite) return `${test.testName}: Rewrite Required (${submission.grade}/20)`;
                return `${test.testName}: ${submission.grade}/20`;
            }).join('; ');
            
            const closeBookStatus = closeBookTest ? (() => {
                const submission = submissions[closeBookTest.id];
                if (!submission || !submission.submitted) return 'Not Submitted';
                return `${submission.grade}/${closeBookTest.totalGrade}`;
            })() : 'N/A';
            
            const verseStatus = verseTest ? (() => {
                const submission = submissions[verseTest.id];
                if (!submission || !submission.submitted) return 'Not Submitted';
                return `${submission.grade}/${verseTest.totalGrade}`;
            })() : 'N/A';

            // Collect grader information
            const graderInfo = [];
            for (const test of tests) {
                const submission = submissions[test.id];
                if (submission && submission.assignedGraderId) {
                    let graderName = '';
                    if (submission.assignedGraderName) {
                        graderName = submission.assignedGraderName;
                    } else {
                        const grader = allGraders[submission.assignedGraderId];
                        graderName = grader ? (grader.initiatedName || `${grader.firstName} ${grader.lastName}`) : 'Grader not found';
                    }
                    
                    const isInactive = submission.graderRemovedFromModule;
                    graderInfo.push(`${test.testName}: ${graderName}${isInactive ? ' (Inactive)' : ''}`);
                }
            }

            return [
                student.initiatedName || `${student.firstName} ${student.lastName}`,
                moduleResult,
                finalGrade.toFixed(1),
                totalServiceHours,
                openBookStatus,
                closeBookStatus,
                verseStatus,
                graderInfo.join('; '),
                failureReasons.join('; ')
            ];
        });

        return [headers, ...rows].map(row => 
            row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        ).join('\n');
    };

    const renderFinalResultTable = () => {
        return (
            <Segment>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <Header as="h3">Student Final Results</Header>
                    <Button
                        primary
                        icon="download"
                        content="Export to CSV"
                        loading={isExportingCSV}
                        disabled={isExportingCSV}
                        onClick={exportToCSV}
                    />
                </div>
                <Table celled>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Student Name</Table.HeaderCell>
                            <Table.HeaderCell>Actions</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {students.map(student => (
                            <Table.Row key={student.id}>
                                <Table.Cell>
                                    {student.initiatedName ? `${student.initiatedName}` : `${student.firstName} ${student.lastName}`}
                                </Table.Cell>
                                <Table.Cell>
                                    <Button
                                        primary
                                        size="small"
                                        loading={isLoadingFinalResult && selectedStudentId === student.id}
                                        disabled={isLoadingFinalResult}
                                        onClick={() => {
                                            setSelectedStudentId(student.id);
                                            setIsLoadingFinalResult(true);
                                            // Small delay to show loading state
                                            setTimeout(() => {
                                                setIsFinalResultModalOpen(true);
                                                setIsLoadingFinalResult(false);
                                            }, 100);
                                        }}
                                    >
                                        {isLoadingFinalResult && selectedStudentId === student.id ? 'Loading...' : 'Show Final Result'}
                                    </Button>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </Segment>
        );
    };

    const panes = [
        ...testStatuses.map(testStatus => ({
            menuItem: testStatus.test.testName,
            render: () => (
                <Tab.Pane>
                    <Header as="h3">{testStatus.test.testName}</Header>
                    {renderTestTable(testStatus)}
                </Tab.Pane>
            )
        })),
        {
            menuItem: 'Final Result',
            render: () => (
                <Tab.Pane>
                    <Header as="h3">Final Results</Header>
                    {renderFinalResultTable()}
                </Tab.Pane>
            )
        }
    ];

    return (
        <div>
            <Header as="h2">Module Status</Header>
            {testStatuses.length === 0 ? (
                <Message info>No tests found in this module.</Message>
            ) : (
                <>
                    <div style={{ 
                        width: '100%',
                        marginBottom: '1rem'
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: '0',
                            borderBottom: '1px solid #d4d4d5',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px 4px 0 0'
                        }}>
                            {panes.map((pane, index) => {
                                const isFirstRow = index < 3;
                                const isLastRow = index >= 3;
                                const isActive = activeTabIndex === index;
                                
                                return (
                                    <div
                                        key={index}
                                        onClick={() => setActiveTabIndex(index)}
                                        style={{
                                            padding: '0.92857143em 0.5em',
                                            cursor: 'pointer',
                                            borderBottom: isActive ? '2px solid #2185d0' : (isFirstRow ? '1px solid #e0e0e0' : '2px solid transparent'),
                                            backgroundColor: isActive ? '#ffffff' : isFirstRow ? '#f8f9fa' : '#f0f0f0',
                                            color: isActive ? '#2185d0' : '#666',
                                            fontWeight: isActive ? 'bold' : 'normal',
                                            textAlign: 'center',
                                            fontSize: '0.9em',
                                            lineHeight: '1.2',
                                            wordWrap: 'break-word',
                                            hyphens: 'auto',
                                            minHeight: '3em',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRight: index % 3 !== 2 ? '1px solid #e0e0e0' : 'none',
                                            position: 'relative',
                                            transition: 'all 0.2s ease',
                                            boxShadow: isActive ? '0 -2px 4px rgba(0,0,0,0.1)' : 'none'
                                        }}
                                        title={pane.menuItem}
                                        onMouseEnter={(e) => {
                                            if (!isActive) {
                                                e.currentTarget.style.backgroundColor = '#e8f4fd';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isActive) {
                                                e.currentTarget.style.backgroundColor = isFirstRow ? '#f8f9fa' : '#f0f0f0';
                                            }
                                        }}
                                    >
                                        {pane.menuItem}
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ padding: '1rem 0' }}>
                            {panes[activeTabIndex]?.render()}
                        </div>
                    </div>
                    {renderSubmissionModal()}
                    {renderExamLogsModal()}
                    {selectedStudentId && (
                        <FinalResultModal
                            moduleId={moduleId}
                            studentId={selectedStudentId}
                            isOpen={isFinalResultModalOpen}
                            onClose={() => {
                                setIsFinalResultModalOpen(false);
                                setSelectedStudentId(null);
                                setIsLoadingFinalResult(false);
                            }}
                        />
                    )}
                    {selectedReassignSubmission && (
                        <ReassignModal
                            isOpen={isReassignModalOpen}
                            onClose={() => {
                                setIsReassignModalOpen(false);
                                setSelectedReassignSubmission(null);
                            }}
                            submission={selectedReassignSubmission.submission}
                            studentId={selectedReassignSubmission.studentId}
                            testId={selectedReassignSubmission.testId}
                            moduleId={moduleId}
                            availableGraders={Object.values(graders)}
                            currentGrader={selectedReassignSubmission.submission.assignedGraderId ? 
                                allGraders[selectedReassignSubmission.submission.assignedGraderId] : undefined}
                            onReassignSuccess={handleReassignSuccess}
                        />
                    )}
                    <Confirm
                        open={showReenableConfirm}
                        onCancel={() => {
                            setShowReenableConfirm(false);
                            setReenableTarget(null);
                        }}
                        onConfirm={handleReenableExam}
                        content={`Are you sure you want to re-enable the exam for ${reenableTarget?.studentName ?? 'this student'}? They will be able to resume where they left off.`}
                        confirmButton="Yes, Re-enable"
                    />
                </>
            )}
        </div>
    );
};

export default ViewStatus;